import {
  Play, Square, Loader2, TerminalSquare, Terminal, Pencil, Trash2, UserPlus,
} from 'lucide-react';
import type { ContextMenuItem } from '@/components/shared/ContextMenu';
import type { TunnelConfig, TunnelStatus } from '@/types';

export interface TunnelActionDeps {
  onStart: () => void;
  onStop: () => void;
  onOpenTerminal: () => void;
  onCopySSHCommand: () => void;
  onEdit: () => void;
  onShare: () => void;
  onDelete: () => void;
}

export function buildTunnelActions(
  tunnel: TunnelConfig,
  status: TunnelStatus,
  deps: TunnelActionDeps,
  activeLocalPort?: number,
): ContextMenuItem[] {
  const isRunning = status === 'running';
  const isStarting = status === 'starting';
  const effectiveLocalPort = activeLocalPort ?? tunnel.localPort ?? undefined;

  return [
    // Featured primary action — Start / Stop tunnel.
    {
      label: isStarting ? 'Starting…' : isRunning ? 'Stop Tunnel' : 'Start Tunnel',
      icon: isStarting ? Loader2 : isRunning ? Square : Play,
      onClick: isRunning ? deps.onStop : deps.onStart,
      disabled: isStarting,
      primary: true,
      sublabel: isRunning
        ? `Listening on 127.0.0.1:${effectiveLocalPort ?? '?'}`
        : isStarting
          ? 'Please wait…'
          : `${tunnel.username}@${tunnel.host}:${tunnel.port}`,
    },
    { label: '', separator: true, sectionTitle: 'Operations' },
    {
      label: 'Open Terminal',
      icon: TerminalSquare,
      onClick: deps.onOpenTerminal,
    },
    {
      label: 'Copy SSH Command',
      icon: Terminal,
      onClick: deps.onCopySSHCommand,
    },
    // Owner-only management; shared recipients only see the operational
    // section above (Start/Stop, Open Terminal, Copy SSH Command).
    ...(tunnel.shared ? [{ label: '', separator: true, sectionTitle: 'Shared with you' } as ContextMenuItem] : [
      { label: '', separator: true, sectionTitle: 'Manage' } as ContextMenuItem,
      { label: 'Edit Tunnel…', icon: Pencil, onClick: deps.onEdit } as ContextMenuItem,
      { label: 'Share…', icon: UserPlus, onClick: deps.onShare } as ContextMenuItem,
      { label: '', separator: true, sectionTitle: 'Danger Zone' } as ContextMenuItem,
      {
        label: 'Delete',
        icon: Trash2,
        danger: true,
        onClick: deps.onDelete,
        // Prevent deleting a running tunnel — consistent with the existing UX.
        disabled: isRunning,
      } as ContextMenuItem,
    ]),
  ];
}
